import { useState } from 'react'
import { Text, UnstyledButton } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import type { PaymentMethod } from '@/model/middleware-types'
import { downloadAttachment } from '@/utils/downloadAttachment'
import {
  PILL_STYLE_AMBER,
  PILL_STYLE_GREEN,
  PILL_STYLE_RED,
  formatCalendarDate,
  formatCents,
  type StatusPillStyle,
} from '../utils'
import { StatusPill } from './StatusPill'

// One card per insurance payment row (mockup 03). Structural mirror of the wire
// shape in case-detail.contract.ts (caseDetailPaymentSchema) — ledger codes stay
// plain strings there, so unknown values fall back to the raw code.
export type CaseDetailPayment = {
  id: number
  paymentType: string
  method: string
  status: string
  amountCents: number
  allowedCents: number
  patientRespCents: number
  payerName?: string
  paymentDate?: string
  depositDate?: string
  stripePaymentIntentId?: string
  externalRef?: string
  checkNumber?: string
  accountLast4?: string
  eobDocumentId?: string
  processedAt?: string
  recordedBy?: string
  failureReason?: string
}

const METHOD_LABELS: Record<PaymentMethod, string> = {
  card: 'Card',
  vcc: 'VCC',
  ach_eft: 'ACH',
  check: 'Check',
}

function methodLabel(method: string): string {
  return METHOD_LABELS[method as PaymentMethod] ?? method
}

function statusStyle(status: string): StatusPillStyle {
  if (status === 'failed') return PILL_STYLE_RED
  if (status === 'processing' || status === 'refunded') return PILL_STYLE_AMBER
  return PILL_STYLE_GREEN
}

function PaymentCard({ payment }: { payment: CaseDetailPayment }) {
  const facts: string[] = []
  if (payment.stripePaymentIntentId) facts.push(`Stripe ${payment.stripePaymentIntentId}`)
  if (payment.accountLast4) facts.push(`····${payment.accountLast4}`)
  if (payment.externalRef) facts.push(`Trace ${payment.externalRef}`)
  if (payment.checkNumber) facts.push(`Check #${payment.checkNumber}`)
  if (payment.paymentDate) facts.push(`Paid ${formatCalendarDate(payment.paymentDate)}`)
  if (payment.depositDate) facts.push(`Deposited ${formatCalendarDate(payment.depositDate)}`)
  if (payment.payerName) facts.push(payment.payerName)

  const feesCents = payment.allowedCents - payment.patientRespCents - payment.amountCents
  if (feesCents > 0) facts.push(`Fees ${formatCents(feesCents)}`)

  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusPill style={statusStyle(payment.status)}>
            {payment.status.toUpperCase()}
          </StatusPill>
          <Text size="sm" fw={600} c="dark">
            {methodLabel(payment.method)}
          </Text>
        </div>
        <Text size="sm" fw={700} c="dark">
          {formatCents(payment.amountCents)}
        </Text>
      </div>
      {facts.length > 0 ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {facts.map((fact) => (
            <Text key={fact} size="xs" c="dimmed">
              {fact}
            </Text>
          ))}
        </div>
      ) : null}
      {payment.failureReason ? (
        <Text size="xs" c="red">
          {payment.failureReason}
        </Text>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        {payment.recordedBy ? (
          <Text size="xs" c="dimmed">
            Recorded by {payment.recordedBy}
          </Text>
        ) : (
          <span />
        )}
        {payment.eobDocumentId ? <EobAttachedLink eobDocumentId={payment.eobDocumentId} /> : null}
      </div>
    </div>
  )
}

/**
 * Opens the attached EOB PDF in a new tab: DocumentReference (the payment row's
 * eob_document_id) → its attachment → blob URL. The attachment lives behind
 * Medplum auth, so a plain <a href> can't render it — download-then-open.
 */
function EobAttachedLink({ eobDocumentId }: { eobDocumentId: string }) {
  const medplum = useMedplum()
  const [opening, setOpening] = useState(false)

  const openEob = async () => {
    setOpening(true)
    try {
      const docRef = await medplum.readResource('DocumentReference', eobDocumentId)
      const url = docRef.content?.[0]?.attachment?.url
      if (!url) throw new Error('The EOB document has no attachment')
      const blob = await downloadAttachment(medplum, url)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to open the EOB',
      })
    } finally {
      setOpening(false)
    }
  }

  return (
    <UnstyledButton onClick={() => void openEob()} disabled={opening} aria-label="EOB attached">
      <Text size="xs" c="green" fw={600} className="hover:underline">
        {opening ? 'Opening EOB…' : 'EOB attached'}
      </Text>
    </UnstyledButton>
  )
}

export function PaymentCards({ payments }: { payments: CaseDetailPayment[] }) {
  const insurancePayments = payments.filter((payment) => payment.paymentType === 'insurance')

  if (insurancePayments.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 px-4 py-6 text-center">
        <Text size="sm" c="dimmed">
          No insurance payments recorded
        </Text>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {insurancePayments.map((payment) => (
        <PaymentCard key={payment.id} payment={payment} />
      ))}
    </div>
  )
}
